import * as React from 'react';
import debounce from 'lodash.debounce';
import classNames from 'classnames';
import merge from 'lodash.merge';
import Highcharts from 'highcharts';
import IntersectionObserver from 'react-intersection-observer';
import {Text, Currency} from 'components/UIKit';
import styles from './CardChart.scss';
import {CHART_OPTIONS, HIGHCHARTS_OPTIONS} from './CardChartOptions';

const TITLE_HEIGHT = 58;

export interface CardChartProps {
  id: string;
  data: any;
  total: number;
  title: string;
  loading?: boolean;
  className?: string;
}

export interface CardChartState {
  chartsReady: boolean;
  containerHeight: number | null;
}

export class CardChart extends React.Component<CardChartProps> {
  chart: Highcharts.Chart | null = null;
  chartNode!: HTMLElement;
  container: React.RefObject<HTMLDivElement> = React.createRef();

  handleResize = debounce(() => {
    if (this.chart && this.container.current) {
      const height = (this.container.current as HTMLElement).clientHeight - TITLE_HEIGHT;
      this.chart.setSize(null, height);
    }
  }, 50);

  buildChart(): Highcharts.Chart {
    const {id, data: chartData} = this.props;

    const data = Object.keys(chartData).map((date) => [new Date(date).getTime(), chartData[date]]);

    const chartOptions = merge(
      {
        chart: {
          renderTo: this.chartNode,
          height: (this.container.current as HTMLElement).clientHeight - TITLE_HEIGHT,
        },
        series: [
          {
            id,
            type: 'area',
            data,
          },
        ],
      },
      CHART_OPTIONS,
      HIGHCHARTS_OPTIONS,
    );

    return new Highcharts.Chart(chartOptions);
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleVisibilityChange = (inView) => {
    if (inView && !this.chart) {
      this.chart = this.buildChart();
      setTimeout(() => (this.chart as Highcharts.Chart).reflow(), 1);
    }
  };

  setupChart = (node: HTMLElement | null): void => {
    if (!node) {
      return;
    }

    this.chartNode = node;
  };

  shouldComponentUpdate(nextProps) {
    return nextProps.id !== this.props.id;
  }

  render() {
    const {total, title, className} = this.props;

    return (
      <IntersectionObserver threshold={1} triggerOnce onChange={this.handleVisibilityChange}>
        {({ref: intersectionRef}) => (
          <div ref={this.container} className={classNames(className, styles.container)}>
            <div ref={intersectionRef} className={styles.title}>
              <Currency value={total} display="bold" />
              <Text type="smaller" text={title} component="p" resetMargin />
            </div>
            <div ref={this.setupChart} />
          </div>
        )}
      </IntersectionObserver>
    );
  }
}
